import { DOCUMENT, inject, Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root',
})
export class MediaElementsService {
    private readonly document = inject<Document>(DOCUMENT);

    private get widgetsLoader(): HTMLElement {
        return this.document.querySelector('website-widgets-loader');
    }

    public waitForMediaElements(): Promise<void[]> {
        const images = this.getPendingImages();
        const videos = this.getPendingVideos();

        const imagesLoadingPromises = images.map((image) => this.waitForImageToLoad(image));
        const videosLoadingPromises = videos.map((video) => this.waitForVideoToLoad(video));

        return Promise.all([...imagesLoadingPromises, ...videosLoadingPromises]);
    }

    private getPendingImages(): HTMLImageElement[] {
        const images: HTMLImageElement[] = Array.from(
            this.widgetsLoader.querySelectorAll('img[loading=lazy], img.lazyload')
        );

        return images.filter((image) => {
            return this.isElementVisible(image) && !image.complete;
        });
    }

    private getPendingVideos(): HTMLVideoElement[] {
        const videos: HTMLVideoElement[] = Array.from(this.widgetsLoader.querySelectorAll('video'));

        return videos.filter((video) => {
            return this.isElementVisible(video);
        });
    }

    private isElementVisible(element: HTMLElement) {
        return element.offsetParent !== null;
    }

    private waitForImageToLoad(imageElement: HTMLImageElement): Promise<void> {
        return new Promise<void>((resolve) => {
            const callback = () => {
                imageElement.removeEventListener('load', callback);
                imageElement.removeEventListener('error', callback);
                resolve();
            };
            imageElement.addEventListener('load', callback);
            imageElement.addEventListener('error', callback);
            imageElement.setAttribute('loading', 'eager');
        });
    }

    private waitForVideoToLoad(videoElement: HTMLVideoElement): Promise<void> {
        return new Promise<void>((resolve) => {
            const callback = () => {
                videoElement.removeEventListener('canplay', callback);
                videoElement.removeEventListener('error', callback);
                resolve();
            };
            videoElement.addEventListener('canplay', callback);
            videoElement.addEventListener('error', callback);

            if (videoElement.poster) {
                const posterElement = new Image();
                this.waitForImageToLoad(posterElement).then(() => {
                    callback();
                });
                posterElement.src = videoElement.poster;
            }
        });
    }
}
